import type { IconName } from './components/nav/icons'
import type { OwnerInboxCounts } from './types/owner-inbox'

export type Role = 'client' | 'admin' | 'owner'
export type NavGroup = 'personal' | 'management' | 'owner'

export interface NavItem {
  key: string
  label: string
  href: string
  icon: IconName
  group: NavGroup
  roles: Role[]
  // Адрес вне SPA (серверная ERB-страница). Такой пункт рендерится обычной <a>:
  // Inertia <Link> получил бы в ответ HTML вместо Inertia-ответа и сломал переход.
  external?: boolean
}

const ALL: Role[] = ['client', 'admin', 'owner']
const STAFF: Role[] = ['admin', 'owner']

export const NAV_ITEMS: NavItem[] = [
  // «Сегодня» — домашний экран владельца (спека 2026-10-11-lk-audit-minor-owner-today).
  // В группе personal и первым: homeHrefFor берёт первый личный пункт, шапка рисует
  // личные слева направо — так логотип ведёт на /owner, а «Сегодня» стоит первым.
  { key: 'owner_today',    label: 'Сегодня',      href: '/owner',          icon: 'home',      group: 'personal',   roles: ['owner'] },
  { key: 'schedule',       label: 'Расписание',   href: '/schedule',       icon: 'calendar',  group: 'personal',   roles: ALL },
  { key: 'packages',       label: 'Пакеты',       href: '/packages',       icon: 'package',   group: 'personal',   roles: ALL },
  { key: 'profile',        label: 'Профиль',      href: '/profile',        icon: 'user',      group: 'personal',   roles: ALL },
  { key: 'notifications',  label: 'Уведомления',  href: '/notifications',  icon: 'bell',      group: 'personal',   roles: ALL },
  // Иконка 'user' занята пунктом «Профиль» рядом — берём 'users' (анкета =
  // публичная карточка среди других преподавателей в каталоге).
  { key: 'card',           label: 'Анкета',       href: '/profile/card',   icon: 'users',     group: 'personal',   roles: ALL },
  // Акции — отдельный раздел, а не блоки внутри профиля. В десктопной шапке это
  // обычная ссылка, в мобильном таб-баре пункт не помещается и уходит в «Ещё»
  // (см. personalOverflow в BottomTabBar.vue). «Анкета» рядом у преподавателя —
  // отдельная вкладка таб-бара, остальные роли видят её в «Ещё».
  { key: 'promos',         label: 'Акции',        href: '/profile/promos', icon: 'gift',      group: 'personal',   roles: ALL },
  // Аренда актового зала — публичная ERB-страница /zal, поэтому external.
  { key: 'hall',           label: 'Зал',          href: '/zal',            icon: 'stage',     group: 'personal',   roles: ALL, external: true },
  // Управляемый вход (спека 2026-08-23-door-access): admin договором не получает
  // прав на дверь нигде — roles здесь СТРОГО ['client', 'owner'], не ALL.
  { key: 'door',           label: 'Дверь',        href: '/door',           icon: 'door',      group: 'personal',   roles: ['client', 'owner'] },
  { key: 'passes',         label: 'Пропуска',     href: '/profile/passes', icon: 'key',       group: 'personal',   roles: ['client', 'owner'] },
  // Не «Расписание»: так у владельца подписана вкладка /schedule, а это другой экран —
  // все брони по кабинетам с закрытием слотов.
  { key: 'admin_schedule', label: 'Сетка броней', href: '/manage/schedule', icon: 'calendar',  group: 'management', roles: STAFF },
  { key: 'admin_users',    label: 'Пользователи', href: '/manage/users',    icon: 'users',     group: 'management', roles: STAFF },
  { key: 'admin_packages', label: 'Пакеты (все)', href: '/manage/packages', icon: 'package',   group: 'management', roles: STAFF },
  // Тарифы и Настройки — в дропдауне «Управление» (group management), но СТРОГО владелец
  // (roles ['owner']): admin видит остальные management-пункты, но не эти; бэкенд дублирует
  // гейт (Owner::TariffsController / Owner::SettingsController → require_owner).
  // Точка входа во владельческие разделы: карта фич со ссылками на профильные
  // экраны. Стоит первой среди owner-пунктов — из неё расходятся остальные.
  { key: 'owner_features', label: 'Как всё работает', href: '/owner/features', icon: 'settings', group: 'management', roles: ['owner'] },
  { key: 'owner_tariffs',  label: 'Тарифы',       href: '/owner/tariffs',  icon: 'price-tag', group: 'management', roles: ['owner'] },
  { key: 'owner_settings', label: 'Настройки коворкинга', href: '/owner/settings', icon: 'settings', group: 'management', roles: ['owner'] },
  { key: 'owner_notifications', label: 'Оповещения', href: '/owner/notification_settings', icon: 'bell', group: 'management', roles: ['owner'] },
  { key: 'owner_catalog', label: 'Агрегатор', href: '/owner/catalog', icon: 'users', group: 'management', roles: ['owner'] },
  { key: 'owner_reviews', label: 'Отзывы', href: '/owner/reviews', icon: 'star', group: 'management', roles: ['owner'] },

  { key: 'owner_payments', label: 'Платежи', href: '/owner/payments', icon: 'wallet', group: 'management', roles: ['owner'] },
  // Финансы — отчёт по деньгам клуба (ручной ввод + онлайн-оплаты). Стоит рядом
  // с «Платежами»: там технический журнал попыток, здесь прибыль за период.
  { key: 'owner_finance',  label: 'Финансы',      href: '/owner/finance',  icon: 'wallet',    group: 'management', roles: ['owner'] },
  { key: 'owner_door',     label: 'Журнал двери', href: '/owner/door', icon: 'door', group: 'management', roles: ['owner'] },
  { key: 'owner_hall_requests', label: 'Заявки на зал', href: '/owner/hall_requests', icon: 'stage', group: 'management', roles: ['owner'] },
  { key: 'owner_consult_requests', label: 'Заявки на консультацию', href: '/owner/consult_requests', icon: 'chat', group: 'management', roles: ['owner'] },
]

// Ключи разделов, которые имеют смысл только там, где коворкинг есть физически.
// Гейт дублируется на бэке (VisitorLocality#require_coworking_zone) — здесь он про
// то, чтобы человеку не предлагали ссылку, которая приведёт его в редирект.
const COWORKING_KEYS = ['schedule', 'packages']

// Ключи разделов управляемого входа. Пункты видны, только когда раскатка открыта
// (settings.door_access_open) И человек в зоне обслуживания: без брони дверь не
// откроется, а брони вне зоны нет — ссылка привела бы в бессмысленный экран.
// Владельцу пункты видны всегда: он и проверяет дверь.
const DOOR_KEYS = ['door', 'passes']

export function navFor(role: Role, coworking = true, doorOpen = true): NavItem[] {
  const doorVisible = role === 'owner' || (doorOpen && coworking)
  return NAV_ITEMS.filter(
    (item) =>
      item.roles.includes(role) &&
      (coworking || !COWORKING_KEYS.includes(item.key)) &&
      (doorVisible || !DOOR_KEYS.includes(item.key)),
  )
}

export function groupsFor(role: Role, coworking = true, doorOpen = true): Record<NavGroup, NavItem[]> {
  const items = navFor(role, coworking, doorOpen)
  return {
    personal: items.filter((i) => i.group === 'personal'),
    management: items.filter((i) => i.group === 'management'),
    owner: items.filter((i) => i.group === 'owner'),
  }
}

// Адрес логотипа: он не может вести в раздел, закрытый для этого человека
// (иногороднему /schedule отдаёт редирект). Хелпер один на TopBar и AppLayout —
// иначе один из вызовов рано или поздно забудет очередной аргумент-гейт.
export function homeHrefFor(role: Role, coworking = true, doorOpen = true): string {
  return groupsFor(role, coworking, doorOpen).personal[0]?.href ?? '/profile'
}

export interface NavSection {
  key: string
  title: string
  items: NavItem[]
}

// Таб-бар владельца на телефоне: «Сегодня», клиентское «Расписание», «Дверь» (+ «Ещё»).
// Клиентские «Пакеты», «Профиль», «Пропуска» владельцу почти не нужны — они в «Ещё».
const OWNER_TAB_KEYS = ['owner_today', 'schedule', 'door']
// «Ещё» владельца: сверху рабочие экраны на каждый день, ниже — редкие настройки,
// в конце — «Личное»: клиентские разделы самого владельца (его профиль, пакеты,
// пропуска). Они рядом с «Выйти», как аккаунт в любом меню, и не мешаются с работой.
// Порядок списков — порядок на экране. Пункт, не попавший ни сюда, ни во вкладки,
// ловит navigation.spec.ts («ровно в одно место»).
const OWNER_DAILY_KEYS = [
  'admin_schedule', 'admin_users', 'owner_payments', 'owner_catalog', 'owner_reviews', 'owner_hall_requests',
  'owner_consult_requests',
  'owner_door', 'admin_packages',
]
const OWNER_SETTINGS_KEYS = ['owner_tariffs', 'owner_settings', 'owner_notifications', 'owner_finance', 'owner_features']
const OWNER_PERSONAL_KEYS = ['profile', 'notifications', 'packages', 'passes', 'card', 'promos', 'hall']

function pick(items: NavItem[], keys: string[]): NavItem[] {
  return keys.map((key) => items.find((item) => item.key === key)).filter((item): item is NavItem => !!item)
}

// doorOpen не нужен: владельцу дверь видна всегда (см. navFor).
export function ownerTabsFor(coworking = true): NavItem[] {
  return pick(navFor('owner', coworking), OWNER_TAB_KEYS)
}

export function ownerSheetFor(coworking = true): NavSection[] {
  const items = navFor('owner', coworking)
  return [
    { key: 'daily', title: 'Каждый день', items: pick(items, OWNER_DAILY_KEYS) },
    { key: 'settings', title: 'Настройки', items: pick(items, OWNER_SETTINGS_KEYS) },
    { key: 'personal', title: 'Личное', items: pick(items, OWNER_PERSONAL_KEYS) },
  ]
}

// Бейджи пунктов владельца из общего пропа owner_inbox. Ноль не рисуется; дверь —
// не число, а факт «не на связи», поэтому «!».
export function inboxBadges(inbox: OwnerInboxCounts | undefined): Record<string, string> {
  if (!inbox) return {}
  const counts: Record<string, number> = {
    owner_today: inbox.total,
    owner_catalog: inbox.catalog,
    owner_reviews: inbox.reviews,
    owner_hall_requests: inbox.hall_requests,
    owner_consult_requests: inbox.consult_requests,
    owner_payments: inbox.payments,
    owner_door: inbox.door_offline ? 1 : 0,
  }
  return Object.fromEntries(
    Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => [key, key === 'owner_door' ? '!' : count > 99 ? '99+' : String(count)]),
  )
}
